import React, { useCallback, useEffect, useRef, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { FaCheckCircle, FaCloudDownloadAlt, FaCloudUploadAlt, FaFileAlt, FaSpinner } from "react-icons/fa";
import axios from "../../configApi/axiosConfig";
import { APIURL } from "../../configApi/apiConfig";

const ESTADO_CONFIG = {
  PENDIENTE:   { label: "Pendiente",   color: "bg-orange-100 text-orange-700  border-orange-300" },
  DESCARGADO:  { label: "Descargado",  color: "bg-blue-100   text-blue-700    border-blue-300"   },
  SUBIDO:      { label: "Subido",      color: "bg-green-100  text-green-700   border-green-300"  },
  REEMPLAZADO: { label: "Reemplazado", color: "bg-purple-100 text-purple-700  border-purple-300" },
};

const periodoActual = () => new Date().toISOString().slice(0, 7);

export function MiDocumento() {
  const [periodo, setPeriodo]         = useState(periodoActual());
  const [datos, setDatos]             = useState(null);
  const [cargando, setCargando]       = useState(false);
  const [subiendo, setSubiendo]       = useState(false);
  const [archivoFirmado, setArchivoFirmado] = useState(null);
  const inputRef = useRef(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const res = await axios.get(APIURL.docMen_miDocumento(periodo));
      setDatos(res.data);
    } catch (e) {
      enqueueSnackbar("Error al cargar el documento del mes.", { variant: "error" });
    } finally {
      setCargando(false);
    }
  }, [periodo]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleDescargar = async () => {
    if (!datos?.documentoBase) return;
    window.open(datos.documentoBase.urlArchivo, "_blank");
    try {
      await axios.post(APIURL.docMen_registrarDescarga(), {
        idDocumentoBase: datos.documentoBase.idDocumentoBase,
        periodo,
      });
      await cargar();
    } catch { /* no-op */ }
  };

  const handleSubir = async () => {
    if (!archivoFirmado) {
      enqueueSnackbar("Selecciona un archivo primero.", { variant: "warning" });
      return;
    }
    setSubiendo(true);
    try {
      const form = new FormData();
      form.append("file", archivoFirmado);
      form.append("idDocumentoBase", String(datos.documentoBase.idDocumentoBase));
      form.append("periodo", periodo);
      const res = await axios.post(APIURL.docMen_subirFirmado(), form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      enqueueSnackbar(res.data.mensaje, { variant: "success" });
      setArchivoFirmado(null);
      if (inputRef.current) inputRef.current.value = "";
      await cargar();
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.message || "Error al subir el archivo.", { variant: "error" });
    } finally {
      setSubiendo(false);
    }
  };

  const estado = datos?.estado ?? "PENDIENTE";
  const estadoCfg = ESTADO_CONFIG[estado] ?? ESTADO_CONFIG.PENDIENTE;

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-8">
      <div className="max-w-2xl mx-auto space-y-6">

        {/* Encabezado */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Documento Mensual</h1>
            <p className="text-slate-500 text-sm">Descarga, firma y sube el documento correspondiente a tu perfil.</p>
          </div>
          <input
            type="month"
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 bg-white shadow-sm focus:ring-2 focus:ring-blue-300 focus:outline-none"
          />
        </div>

        {cargando ? (
          <div className="flex justify-center py-20 text-slate-400">
            <FaSpinner className="animate-spin text-4xl" />
          </div>
        ) : !datos?.documentoBase ? (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-10 text-center text-slate-400">
            <FaFileAlt className="text-5xl mx-auto mb-3 text-slate-300" />
            <p className="font-medium">No hay documento disponible para el periodo <strong>{periodo}</strong>.</p>
            <p className="text-sm mt-1">Consulta con el administrador.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">

            {/* Info documento */}
            <div className="p-6 border-b border-slate-100 flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                <FaFileAlt className="text-blue-600 text-xl" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-bold text-slate-800 text-lg leading-tight truncate">
                  {datos.documentoBase.nombreDocumento}
                </h2>
                {datos.documentoBase.descripcion && (
                  <p className="text-slate-500 text-sm mt-0.5">{datos.documentoBase.descripcion}</p>
                )}
                <p className="text-xs text-slate-400 mt-1">Periodo: {periodo}</p>
              </div>
              <span className={`text-xs font-bold px-3 py-1 rounded-full border ${estadoCfg.color}`}>
                {estadoCfg.label}
              </span>
            </div>

            {/* Acciones */}
            <div className="p-6 space-y-4">

              {/* Descargar */}
              <button
                onClick={handleDescargar}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl shadow transition-all active:scale-95"
              >
                <FaCloudDownloadAlt className="text-lg" />
                Descargar documento base
              </button>

              {/* Subir firmado */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">
                  {datos.firmado ? "Reemplazar documento firmado" : "Subir documento firmado"}
                </label>
                <div className="flex gap-2">
                  <label className="flex-1 flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors text-sm text-slate-500">
                    <FaFileAlt />
                    {archivoFirmado ? archivoFirmado.name : "Seleccionar archivo..."}
                    <input
                      ref={inputRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="hidden"
                      onChange={(e) => setArchivoFirmado(e.target.files[0] || null)}
                    />
                  </label>
                  <button
                    onClick={handleSubir}
                    disabled={subiendo || !archivoFirmado}
                    className="flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold rounded-xl shadow transition-all active:scale-95"
                  >
                    {subiendo ? <FaSpinner className="animate-spin" /> : <FaCloudUploadAlt />}
                    {subiendo ? "Subiendo..." : "Subir"}
                  </button>
                </div>
              </div>

              {/* Firmado ya subido */}
              {datos.firmado && (
                <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-xl text-sm">
                  <FaCheckCircle className="text-green-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-green-800">Documento firmado subido</p>
                    <p className="text-green-600 text-xs truncate">{datos.firmado.nombreArchivoFirmado}</p>
                    <p className="text-green-500 text-xs">
                      {new Date(datos.firmado.fechaSubida).toLocaleString('es-EC')}
                      {datos.firmado.fechaActualizacion && ` (actualizado: ${new Date(datos.firmado.fechaActualizacion).toLocaleString('es-EC')})`}
                    </p>
                  </div>
                  <a
                    href={datos.firmado.urlArchivoFirmado}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-bold text-green-700 underline hover:text-green-900 flex-shrink-0"
                  >
                    Ver
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
